import type { FeedPost } from '@wlb/shared/types/post';
import { POST_MAX_CHAR_COUNT } from '@wlb/shared/constants';
import { convertPostContentToPlainText } from '@wlb/shared/utils/postContent';

export interface InAppShareRequest {
  postId: string;
  bodyText?: string;
  groupId?: string;
  privacyId?: number;
}

interface InAppShareBodyOptions {
  message: string;
  originalText: string;
  shareUrl: string;
  attribution: string;
}

interface ExternalShareLinkOptions {
  shareUrl: string;
  text: string;
  subject: string;
}

interface PrivateShareMessageOptions {
  message: string;
  shareText: string;
  shareUrl: string;
}

export interface ExternalShareLinks {
  whatsapp: string;
  facebook: string;
  x: string;
  linkedIn: string;
  email: string;
}

export const canQuickReshare = (
  post: Pick<FeedPost, 'allowShare' | 'privacy' | 'postVisibility'>,
): boolean => {
  if (post.allowShare === false) return false;
  const visibility = post.privacy ?? post.postVisibility;
  if (typeof visibility === 'number') return visibility === 1;
  const normalized = String(visibility ?? '').toLowerCase();
  return normalized === '1' || normalized === 'public';
};

const truncate = (value: string, maxLength: number): string => {
  if (value.length <= maxLength) return value;
  if (maxLength <= 1) return value.slice(0, Math.max(0, maxLength));
  return `${value.slice(0, maxLength - 1).trimEnd()}…`;
};

/** Builds a same-origin post URL so signed-in members keep their web session. */
export const buildInternalPostUrl = (
  postId: string,
  origin: string,
): string => new URL(
  `/post/${encodeURIComponent(postId)}`,
  origin,
).toString();

/**
 * The feed API currently returns a reshare as an ordinary text post and does
 * not expose its root-post object. Include an attributed snapshot and deep
 * link so the reshare never loses the content it refers to.
 */
export const buildInAppShareBody = ({
  message,
  originalText,
  shareUrl,
  attribution,
}: InAppShareBodyOptions): string => {
  const cleanMessage = convertPostContentToPlainText(message);
  const cleanOriginal = convertPostContentToPlainText(originalText);
  const fixedSource = `${attribution.trim()}\n${shareUrl.trim()}`;
  const messageSeparatorLength = cleanMessage ? 2 : 0;
  const messageLimit = Math.max(
    0,
    POST_MAX_CHAR_COUNT - fixedSource.length - messageSeparatorLength,
  );
  const boundedMessage = truncate(cleanMessage, messageLimit);
  const originalLimit = Math.max(
    0,
    POST_MAX_CHAR_COUNT
      - fixedSource.length
      - (boundedMessage ? 2 : 0)
      - boundedMessage.length
      - 1,
  );
  const originalSnapshot = truncate(cleanOriginal, originalLimit);
  const source = [
    attribution.trim(),
    originalSnapshot,
    shareUrl.trim(),
  ].filter(Boolean).join('\n');

  if (!boundedMessage) return source;
  return boundedMessage ? `${boundedMessage}\n\n${source}` : source;
};

/** Direct external destinations keep sharing inside the WLB modal. */
export const buildExternalShareLinks = ({
  shareUrl,
  text,
  subject,
}: ExternalShareLinkOptions): ExternalShareLinks => {
  const message = `${text.trim()}\n${shareUrl}`.trim();
  return {
    whatsapp: `https://wa.me/?text=${encodeURIComponent(message)}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
      shareUrl,
    )}`,
    x: `https://twitter.com/intent/tweet?text=${encodeURIComponent(
      text.trim(),
    )}&url=${encodeURIComponent(shareUrl)}`,
    linkedIn: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
      shareUrl,
    )}`,
    email: `mailto:?subject=${encodeURIComponent(
      subject,
    )}&body=${encodeURIComponent(message)}`,
  };
};

export const buildPrivateShareMessage = ({
  message,
  shareText,
  shareUrl,
}: PrivateShareMessageOptions): string => {
  const cleanMessage = convertPostContentToPlainText(message);
  const sharedPost = `${shareText.trim()}\n${shareUrl.trim()}`.trim();
  return cleanMessage ? `${cleanMessage}\n\n${sharedPost}` : sharedPost;
};

/** Builds the shared FeedContext request for an in-app reshare. */
export const buildInAppShareRequest = (
  post: Pick<FeedPost, 'id' | 'groupId'>,
  message: string,
  privacyId?: number,
): InAppShareRequest => {
  const bodyText = convertPostContentToPlainText(message);

  return {
    postId: post.id,
    ...(bodyText ? { bodyText } : {}),
    ...(post.groupId ? { groupId: post.groupId } : {}),
    ...(privacyId != null ? { privacyId } : {}),
  };
};
